export const KEY_GUARD_ACTIVATE = { 'data-key-guard-activate': 'true' };

const ACTIVATE_SELECTOR = '[data-key-guard-activate="true"]';

export const GAME_KEY_CODES: ReadonlySet<string> = new Set([
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'Space',
  'KeyW',
  'KeyA',
  'KeyS',
  'KeyD',
  'ShiftLeft',
  'ShiftRight',
]);

const ACTIVATION_CODES: ReadonlySet<string> = new Set([
  'Enter',
  'NumpadEnter',
  'Space',
]);

const KEY_EVENTS = ['keydown', 'keyup', 'keypress'] as const;

export interface IKeyGuardOptions {
  target: Window;
  onKeyDown: (event: KeyboardEvent) => void;
  onKeyUp: (event: KeyboardEvent) => void;
  onEscape: () => void;
  onRelease: () => void;
  passThrough?: (target: EventTarget | null) => boolean;
}

export interface IKeyGuard {
  dispose: () => void;
}

const holdsCommandModifier = (event: KeyboardEvent) =>
  event.metaKey || event.ctrlKey || event.altKey;

const activatesControl = (event: KeyboardEvent) =>
  ACTIVATION_CODES.has(event.code) &&
  event.target instanceof Element &&
  event.target.closest(ACTIVATE_SELECTOR) !== null;

export const shouldPreventKey = (event: KeyboardEvent) =>
  GAME_KEY_CODES.has(event.code) &&
  !holdsCommandModifier(event) &&
  !activatesControl(event);

const drainHeldKeys = (target: Window, codes: Set<string>) => {
  const stop = () => {
    target.removeEventListener('keydown', onKeyDown, true);
    target.removeEventListener('keyup', onKeyUp, true);
    target.removeEventListener('blur', stop);
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (!codes.has(event.code)) {
      return;
    }

    if (event.repeat) {
      event.stopImmediatePropagation();
      return;
    }

    codes.delete(event.code);

    if (codes.size === 0) {
      stop();
    }
  };

  const onKeyUp = (event: KeyboardEvent) => {
    if (!codes.delete(event.code)) {
      return;
    }

    event.stopImmediatePropagation();

    if (codes.size === 0) {
      stop();
    }
  };

  target.addEventListener('keydown', onKeyDown, true);
  target.addEventListener('keyup', onKeyUp, true);
  target.addEventListener('blur', stop);
};

export const createKeyGuard = ({
  target,
  onKeyDown,
  onKeyUp,
  onEscape,
  onRelease,
  passThrough,
}: IKeyGuardOptions): IKeyGuard => {
  const held = new Map<string, boolean>();
  let disposed = false;

  const handleKeyDown = (event: KeyboardEvent) => {
    if (!held.has(event.code) && passThrough?.(event.target)) {
      return;
    }

    event.stopImmediatePropagation();

    if (event.code === 'Escape') {
      if (!event.repeat) {
        held.set(event.code, false);
        onEscape();
      }
      return;
    }

    if (shouldPreventKey(event)) {
      event.preventDefault();
    }

    if (!held.has(event.code)) {
      if (event.repeat) {
        return;
      }
      held.set(event.code, !activatesControl(event));
    }

    if (held.get(event.code)) {
      onKeyDown(event);
    }
  };

  const handleKeyUp = (event: KeyboardEvent) => {
    const forwarded = held.get(event.code);

    if (forwarded === undefined) {
      return;
    }

    held.delete(event.code);
    event.stopImmediatePropagation();

    if (shouldPreventKey(event)) {
      event.preventDefault();
    }

    if (forwarded) {
      onKeyUp(event);
    }
  };

  const handleKeyPress = (event: KeyboardEvent) => {
    if (passThrough?.(event.target)) {
      return;
    }

    event.stopImmediatePropagation();

    if (shouldPreventKey(event)) {
      event.preventDefault();
    }
  };

  const handlers = {
    keydown: handleKeyDown,
    keyup: handleKeyUp,
    keypress: handleKeyPress,
  };

  const handleBlur = () => {
    held.clear();
    onRelease();
  };

  KEY_EVENTS.forEach((type) =>
    target.addEventListener(type, handlers[type], true),
  );
  target.addEventListener('blur', handleBlur);

  return {
    dispose: () => {
      if (disposed) {
        return;
      }

      disposed = true;
      KEY_EVENTS.forEach((type) =>
        target.removeEventListener(type, handlers[type], true),
      );
      target.removeEventListener('blur', handleBlur);

      if (held.size > 0) {
        drainHeldKeys(target, new Set(held.keys()));
        held.clear();
      }
    },
  };
};
